import React, { useState, useEffect } from 'react';
import { doc, getDoc } from 'firebase/firestore';
import { db } from '../firebase';
import Avatar from './Avatar';
import '../styles/global.css';

const UserProfileModal = ({ userId, onClose }) => {
    const [profile, setProfile] = useState(null);
    const [loading, setLoading] = useState(true);

    const formatDate = (timestamp) => {
        if (timestamp === 0) return 'Acceso Permanente';
        if (!timestamp) return 'S/I';
        // Handle Firestore Timestamp or milliseconds
        const date = timestamp.toDate ? timestamp.toDate() : new Date(timestamp);
        return date.toLocaleDateString('es-ES', {
            year: 'numeric',
            month: 'long',
            day: 'numeric'
        });
    };

    useEffect(() => {
        const fetchProfile = async () => {
            try {
                const docRef = doc(db, 'users', userId);
                const docSnap = await getDoc(docRef);

                if (docSnap.exists()) {
                    setProfile(docSnap.data());
                } else {
                    setProfile({ nick: 'Usuario desconocido', bio: 'Este perfil no existe en la base de datos.' });
                }
            } catch (error) {
                console.error("Error fetching profile:", error);
            } finally {
                setLoading(false);
            }
        };

        if (userId) fetchProfile();
    }, [userId]);

    if (!userId) return null;

    return (
        <div style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: 'rgba(0,0,0,0.85)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 2000,
            backdropFilter: 'blur(8px)'
        }} onClick={onClose}>
            <div style={{
                backgroundColor: 'var(--bg-secondary)',
                color: 'var(--text-primary)',
                padding: '40px 30px',
                borderRadius: '24px',
                width: '95%',
                maxWidth: '450px',
                position: 'relative',
                border: '1px solid var(--border)',
                boxShadow: '0 20px 50px rgba(0,0,0,0.6)',
                maxHeight: '90vh',
                overflowY: 'auto'
            }} onClick={e => e.stopPropagation()}>

                <button
                    onClick={onClose}
                    style={{
                        position: 'absolute',
                        top: '20px',
                        right: '25px',
                        background: 'rgba(255,255,255,0.05)',
                        border: 'none',
                        color: 'var(--text-secondary)',
                        fontSize: '1.2rem',
                        cursor: 'pointer',
                        width: '32px',
                        height: '32px',
                        borderRadius: '50%',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center'
                    }}
                >
                    &times;
                </button>

                {loading ? (
                    <div style={{ padding: '40px 0' }}>
                        <div className="loading-spinner"></div>
                    </div>
                ) : (
                    <>
                        <div style={{ position: 'relative', display: 'inline-block', marginBottom: '20px' }}>
                            <div style={{
                                width: '120px',
                                height: '120px',
                                borderRadius: '50%',
                                background: 'linear-gradient(135deg, var(--accent) 0%, #1a2a44 100%)',
                                display: 'flex',
                                alignItems: 'center',
                                justifyCenter: 'center',
                                fontSize: '4rem',
                                margin: '0 auto',
                                border: '4px solid var(--bg-tertiary)',
                                overflow: 'hidden',
                                boxShadow: '0 8px 16px rgba(0,0,0,0.3)'
                            }}>
                                <Avatar src={profile?.photoURL || profile?.avatar} size={120} />
                            </div>
                            {profile?.isPremium && (
                                <div style={{
                                    position: 'absolute',
                                    bottom: '5px',
                                    right: '5px',
                                    background: 'gold',
                                    borderRadius: '50%',
                                    width: '30px',
                                    height: '30px',
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    fontSize: '1.2rem',
                                    border: '3px solid var(--bg-secondary)',
                                    boxShadow: '0 2px 4px rgba(0,0,0,0.3)'
                                }} title="Usuario Premium">
                                    ⭐
                                </div>
                            )}
                        </div>

                        <h2 style={{ margin: '0 0 5px 0', fontSize: '1.8rem', fontWeight: '800' }}>
                            {profile?.nick || profile?.displayName || 'Usuario'}
                        </h2>

                        <p style={{ margin: '0 0 25px 0', color: 'var(--accent)', fontSize: '1rem', fontWeight: '600', letterSpacing: '0.5px' }}>
                            {profile?.grado || 'Sin Grado Definido'}
                        </p>

                        <div style={{
                            display: 'grid',
                            gridTemplateColumns: '1fr',
                            gap: '12px',
                            textAlign: 'left',
                            marginBottom: '25px'
                        }}>
                            <div style={{ background: 'var(--bg-tertiary)', padding: '12px 15px', borderRadius: '12px' }}>
                                <label style={{ display: 'block', fontSize: '0.7rem', color: 'var(--text-secondary)', textTransform: 'uppercase', marginBottom: '4px', fontWeight: '700' }}>Cuerpo/Servicio</label>
                                <span style={{ fontSize: '0.95rem' }}>{profile?.cuerpoServicio || 'No asignado'}</span>
                            </div>

                            <div style={{ background: 'var(--bg-tertiary)', padding: '12px 15px', borderRadius: '12px' }}>
                                <label style={{ display: 'block', fontSize: '0.7rem', color: 'var(--text-secondary)', textTransform: 'uppercase', marginBottom: '4px', fontWeight: '700' }}>Especialidad</label>
                                <span style={{ fontSize: '0.95rem' }}>{profile?.especialidad || 'No asignada'}</span>
                            </div>

                            <div style={{ background: 'var(--bg-tertiary)', padding: '12px 15px', borderRadius: '12px' }}>
                                <label style={{ display: 'block', fontSize: '0.7rem', color: 'var(--text-secondary)', textTransform: 'uppercase', marginBottom: '4px', fontWeight: '700' }}>Biografía</label>
                                <p style={{ margin: 0, fontSize: '0.9rem', color: 'var(--text-secondary)', lineHeight: '1.4' }}>
                                    {profile?.bio || "Este usuario prefiere mantener el misterio..."}
                                </p>
                            </div>
                        </div>

                        {profile?.interests && profile.interests.length > 0 && profile.interests[0] !== "" && (
                            <div style={{ marginBottom: '25px', textAlign: 'left' }}>
                                <label style={{ display: 'block', fontSize: '0.75rem', color: 'var(--text-secondary)', marginBottom: '8px', paddingLeft: '5px' }}>INTERESES</label>
                                <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px' }}>
                                    {profile.interests.map((interest, idx) => (
                                        <span key={idx} style={{
                                            padding: '4px 12px',
                                            background: 'rgba(255,255,255,0.05)',
                                            borderRadius: '20px',
                                            fontSize: '0.8rem',
                                            border: '1px solid rgba(255,255,255,0.1)'
                                        }}>
                                            {interest}
                                        </span>
                                    ))}
                                </div>
                            </div>
                        )}

                        <div style={{
                            borderTop: '1px solid var(--border)',
                            paddingTop: '20px',
                            display: 'grid',
                            gridTemplateColumns: 'repeat(2, 1fr)',
                            gap: '15px'
                        }}>
                            <div style={{ textAlign: 'left' }}>
                                <label style={{ display: 'block', fontSize: '0.65rem', color: 'var(--text-secondary)', marginBottom: '2px' }}>MIEMBRO DESDE</label>
                                <span style={{ fontSize: '0.8rem', fontWeight: '500' }}>{formatDate(profile?.createdAt)}</span>
                            </div>
                            {profile?.isPremium && (
                                <div style={{ textAlign: 'left' }}>
                                    <label style={{ display: 'block', fontSize: '0.65rem', color: 'var(--text-secondary)', marginBottom: '2px' }}>PREMIUM HASTA</label>
                                    <span style={{ fontSize: '0.8rem', fontWeight: '500', color: 'gold' }}>{formatDate(profile?.premiumEndDate)}</span>
                                </div>
                            )}
                        </div>
                    </>
                )}
            </div>
        </div>
    );
};

export default UserProfileModal;
